import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ApplicationService } from '../services/application.service';

/**
 * Resource-level guard that ensures the current user is actually scoped to the
 * application referenced by the `applicationName` / `Application_Name` route
 * parameter. This prevents a user from manually editing the URL to access an
 * application they are not a member/admin of.
 *
 * NOTE: This only protects navigation in the UI. The backend must enforce the
 * same per-application authorization on every endpoint, since a client-side
 * guard can always be bypassed by calling the API directly.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationAccessGuard {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly applicationService = inject(ApplicationService);

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.hasApplicationAccess(route);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.hasApplicationAccess(route);
  }

  private async hasApplicationAccess(
    route: ActivatedRouteSnapshot
  ): Promise<boolean> {
    // Read whichever parameter the target route uses to identify the app.
    const requestedApplication =
      route.paramMap.get('applicationName') ??
      route.paramMap.get('Application_Name');

    // Routes without an application context (e.g. the applications list) are
    // not application-scoped, so there is nothing to validate here.
    if (!requestedApplication) {
      return true;
    }

    try {
      const user = await this.authService.getCurrentUser();
      const email = user?.username?.replace('DraexlmaierAzureAD_', '');

      if (!email) {
        this.router.navigate(['/public/login']);
        return false;
      }

      const applications = await firstValueFrom(
        this.applicationService.getApplicationByUser(email)
      );

      const isAuthorized = (applications ?? []).some(
        (app) => app.application_name === requestedApplication
      );

      if (!isAuthorized) {
        // User is not scoped to this application: send them back to their
        // own applications list instead of the requested screen.
        this.router.navigate(['/secure/developer/applications']);
        return false;
      }

      return true;
    } catch (err) {
      this.router.navigate(['/public/login']);
      return false;
    }
  }
}
